import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import { runtimeApi } from "../../api/runtimeApi";
import type { ApiKey, ReportDefinition } from "../../api/types";

type Tab = "iframe" | "rest";

export default function IntegrationSample() {
  const [tab, setTab] = useState<Tab>("iframe");
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [reportKey, setReportKey] = useState("");
  const [apiKeyPrefix, setApiKeyPrefix] = useState("");
  const [runResult, setRunResult] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([adminApi.reports.list(), adminApi.apiKeys.list()])
      .then(([r, k]) => {
        const published = r.filter((rep) => rep.status === "PUBLISHED");
        setReports(published);
        setApiKeys(k);
        if (published[0]) setReportKey(published[0].reportKey);
        if (k[0]) setApiKeyPrefix(k[0].keyPrefix);
      })
      .catch(() => undefined);
  }, []);

  const origin = window.location.origin;
  const iframeSrc = `${origin}/embed/reports/${reportKey || "REPORT_KEY"}?apiKey=YOUR_API_KEY`;

  const fetchSnippet = `fetch("${origin}/api/v1/reports/${reportKey || "REPORT_KEY"}/preview", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Api-Key": "YOUR_API_KEY",
  },
  body: JSON.stringify({
    params: { dateFrom: "2026-01-01", dateTo: "2026-01-31" },
    format: "pdf",
  }),
})
  .then((res) => res.json())
  .then((htmlPages) => {
    // htmlPages is a List<String> of rendered HTML pages, same shape
    // reporting-service returns today from POST /reports/preview
    console.log(htmlPages);
  });`;

  const curlSnippet = `curl -X POST "${origin}/api/v1/reports/${reportKey || "REPORT_KEY"}/preview" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{"params": {"dateFrom": "2026-01-01", "dateTo": "2026-01-31"}, "format": "pdf"}'`;

  async function runIt() {
    if (!reportKey) return;
    setRunning(true);
    setError(null);
    setRunResult(null);
    try {
      const key = apiKeys.find((k) => k.keyPrefix === apiKeyPrefix);
      const pages = await runtimeApi.preview(
        reportKey,
        { params: {}, format: "pdf" },
        key?.fullKey
      );
      setRunResult(pages.join("\n\n"));
    } catch (err) {
      setError(
        err instanceof Error
          ? `${err.message} (note: this button uses your key's PREFIX for display only — paste the real secret in "Direct REST call" testing tools; the backend must resolve prefix->key for this demo to work end-to-end)`
          : "Run failed"
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Integration Sample</h1>
      </div>

      <div className="page-panel">
        <p className="help-text">
          This is the reference pattern any client application — such as the ExtJS UAP app
          calling{" "}
          <code>reporting-service</code> today via{" "}
          <code>ReportingServiceWindowController.js</code>'s <code>POST .../reports/preview</code>{" "}
          — uses to plug into ReportForge. Both patterns below are stateless: no session, no
          cookie, just an API key (or tenant JWT) on each request.
        </p>

        <div className="form-row">
          <label>Report</label>
          <select value={reportKey} onChange={(e) => setReportKey(e.target.value)}>
            {reports.length === 0 && <option value="">(no published reports yet)</option>}
            {reports.map((r) => (
              <option key={r.id} value={r.reportKey}>
                {r.displayName} ({r.reportKey})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={tab === "iframe" ? "tab-button active" : "tab-button"}
          onClick={() => setTab("iframe")}
        >
          Embed via iframe
        </button>
        <button
          type="button"
          className={tab === "rest" ? "tab-button active" : "tab-button"}
          onClick={() => setTab("rest")}
        >
          Direct REST call
        </button>
      </div>

      {tab === "iframe" && (
        <div className="page-panel">
          <h3>Embed a report preview</h3>
          <p className="help-text">
            Drop-in embed for host apps that just want a rendered report widget, no REST
            integration required.
          </p>
          <div className="code-block">{`<iframe\n  src="${iframeSrc}"\n  style="width:100%; height:600px; border:0;"\n  title="ReportForge report"\n></iframe>`}</div>
          <h4>Live example</h4>
          {reportKey ? (
            <iframe
              title="Embed preview"
              src={`/embed/reports/${reportKey}`}
              style={{ width: "100%", height: 480, border: "1px solid var(--color-border)", borderRadius: 6 }}
            />
          ) : (
            <p className="help-text">Publish a report to see a live embed here.</p>
          )}
        </div>
      )}

      {tab === "rest" && (
        <div className="page-panel">
          <h3>Call the runtime API directly</h3>
          <p className="help-text">
            Same request/response shape as the legacy <code>/preview</code> and{" "}
            <code>/download</code> endpoints — swap the base URL and use an{" "}
            <code>X-Api-Key</code> header instead of passing <code>fmJwtToken</code> in the body.
          </p>

          <h4>JavaScript (fetch)</h4>
          <div className="code-block">{fetchSnippet}</div>

          <h4>curl</h4>
          <div className="code-block">{curlSnippet}</div>

          <h4>Try it against your own API key</h4>
          <div className="form-row">
            <label>API key</label>
            <select value={apiKeyPrefix} onChange={(e) => setApiKeyPrefix(e.target.value)}>
              {apiKeys.length === 0 && <option value="">(create one under API Keys first)</option>}
              {apiKeys.map((k) => (
                <option key={k.id} value={k.keyPrefix}>
                  {k.label} ({k.keyPrefix}...)
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn-primary" onClick={runIt} disabled={running || !reportKey}>
            {running ? "Running..." : "Run it"}
          </button>

          {error && <div className="error-text" style={{ marginTop: 10 }}>{error}</div>}
          {runResult && (
            <div style={{ marginTop: 10 }}>
              <h4>Response</h4>
              <iframe title="Result" srcDoc={runResult} style={{ width: "100%", height: 400, border: "1px solid var(--color-border)" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
